// src/pages/monitoring/TindakLanjutDetail.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Plus, Upload, Clock, Target, Users, FileText, TrendingUp,
    AlertTriangle, Calendar, Download, AlertCircle, UserCog, RotateCcw,
    ChevronDown, CalendarPlus, XCircle, CheckCircle, Play, Pause, Zap,
    MessageSquare, ExternalLink, Timer, Award, Building2, User, Shield,
    BarChart3, Flag, Edit3, Send, Eye, Info, CalendarCheck, ChevronRight,
} from 'lucide-react';
import {
    Button, Card, Breadcrumb, Modal, Input, Textarea, ProgressBar, Select, Loading,
} from '../../components/ui';
import Badge from '../../components/ui/Badge';
import { monitoringService } from '../../services/monitoring';
import { PpeppRekomendasi, PpeppKpi, HambatanTindakLanjut } from '../../types';
import { useRole } from '../../hooks/useRole';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useAuthStore } from '../../store/authStore';
import { formatDistanceToNow, differenceInCalendarDays } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import toast from 'react-hot-toast';

// ========== TYPES ==========
type TindakLanjutPhase = 'berlangsung' | 'terhambat' | 'menunggu_konfirmasi' | 'selesai' | 'dibatalkan';

interface RiwayatPerpanjanganItem {
    tanggal: string;
    deadline_lama: string | null;
    deadline_baru: string | null;
    alasan: string | null;
    pengubah: string | null;
}

type UnifiedTimelineEventType =
    | 'progress'
    | 'ppepp_created'
    | 'ppepp_cancelled'
    | 'pic_change'
    | 'deadline_extended'
    | 'returned_to_rapim'
    | 'rejection';

interface UnifiedTimelineEvent {
    type: UnifiedTimelineEventType;
    date: Date;
    ppeppIndex?: number;
    ppeppId?: number;
    ppeppStatus?: string;
    isPpeppAktif?: boolean;
    data: any;
}

// ========== CONSTANTS ==========
const JENIS_HAMBATAN_OPTIONS = [
    { value: 'Anggaran belum turun', label: 'Anggaran belum turun' },
    { value: 'Anggaran tidak cukup', label: 'Anggaran tidak cukup' },
    { value: 'SDM tidak tersedia', label: 'SDM tidak tersedia' },
    { value: 'Ketergantungan eksternal', label: 'Ketergantungan eksternal (vendor, pengiriman, dll)' },
    { value: 'Kebijakan berubah', label: 'Kebijakan berubah' },
    { value: 'Vendor/Mitra bermasalah', label: 'Vendor/Mitra bermasalah' },
    { value: 'Lainnya', label: 'Lainnya' },
];

// ========== HELPER FUNCTIONS ==========
const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const formatDateShort = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
    }).format(value);
};

const formatRelativeTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    try {
        return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: idLocale });
    } catch {
        return '';
    }
};

// ========== SIDEBAR COMPONENTS ==========

// Compact Info Row
const InfoRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    subValue?: string;
    highlight?: boolean;
}> = ({ icon, label, value, subValue, highlight }) => (
    <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? 'bg-danger/10 text-danger' : 'bg-neutral-bg text-neutral-text-secondary'
            }`}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-text-secondary">{label}</p>
            <p className={`text-sm font-medium ${highlight ? 'text-danger' : 'text-neutral-text-primary'}`}>{value}</p>
            {subValue && <p className="text-[10px] text-neutral-text-secondary mt-0.5">{subValue}</p>}
        </div>
    </div>
);

// Phase Badge
const PhaseBadge: React.FC<{ phase: TindakLanjutPhase }> = ({ phase }) => {
    const config: Record<TindakLanjutPhase, { label: string; bg: string; text: string; icon: any }> = {
        berlangsung: { label: 'Dalam Proses', bg: 'bg-teal-100', text: 'text-teal-700', icon: Play },
        terhambat: { label: 'Terhambat', bg: 'bg-red-100', text: 'text-red-700', icon: Pause },
        menunggu_konfirmasi: { label: 'Menunggu Konfirmasi', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
        selesai: { label: 'Selesai', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
        dibatalkan: { label: 'Dibatalkan', bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle },
    };
    const c = config[phase] || config.berlangsung;
    const Icon = c.icon;

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>
            <Icon className="w-3.5 h-3.5" />
            {c.label}
        </span>
    );
};

// ========== BERLANGSUNG SIDEBAR ==========
const BerlangSungSidebar: React.FC<{
    data: any;
    progress: number;
    targetDate: string | null;
    isOverdue: boolean;
    daysRemaining: number;
    laporanTerkait: any;
    perluRevisi: boolean;
    isPicAktif?: boolean;
    canConfirmCompletion?: boolean;
}> = ({
    data,
    progress,
    targetDate,
    isOverdue,
    daysRemaining,
    laporanTerkait,
    perluRevisi,
    isPicAktif = false,
    canConfirmCompletion = false,
}) => {
        const progressEntries = Array.isArray(data?.progress) ? data.progress.length : 0;
        const hasNoProgressYet = progress <= 0 && progressEntries === 0 && !perluRevisi;
        const isNoProgressOverdue = hasNoProgressYet && isOverdue;

        const getTindakLanjutStatus = () => {
            if (perluRevisi) return { label: 'Perlu Perbaikan', color: 'amber', icon: Edit3 };
            if (progress >= 100) return { label: 'Menunggu Konfirmasi', color: 'amber', icon: Clock };
            if (isNoProgressOverdue) return { label: 'Belum Ada Progress', color: 'danger', icon: AlertTriangle };
            if (progress >= 75) return { label: 'Hampir Selesai', color: 'success', icon: TrendingUp };
            if (progress >= 50) return { label: 'Berjalan Baik', color: 'info', icon: TrendingUp };
            if (progress >= 25) return { label: 'Sedang Dikerjakan', color: 'teal', icon: Play };
            if (hasNoProgressYet) return { label: 'Belum Ada Progress', color: 'warning', icon: Clock };
            return { label: 'Baru Dimulai', color: 'teal', icon: Play };
        };

        const tlStatus = getTindakLanjutStatus();
        const TlIcon = tlStatus.icon;

        const getStatusDescription = () => {
            if (perluRevisi) {
                return 'Pengajuan selesai ditolak. Harap perbaiki dan ajukan kembali.';
            }
            if (progress >= 100) {
                if (isPicAktif) {
                    return 'Progress sudah 100%. Gunakan tombol Ajukan Konfirmasi Selesai untuk meminta persetujuan pimpinan.';
                }
                if (canConfirmCompletion) {
                    return 'PIC telah menyelesaikan progress 100%. Tinjau dan konfirmasi penyelesaian melalui panel tindakan.';
                }
                return 'Progress sudah 100%. Menunggu konfirmasi penyelesaian dari pimpinan.';
            }
            if (isNoProgressOverdue) {
                return `Tindak lanjut sudah ditetapkan, tetapi belum ada progress yang dicatat dan target sudah lewat ${Math.abs(daysRemaining || 0)} hari. Segera perbarui progress atau laporkan hambatan bila pekerjaan belum dapat berjalan.`;
            }
            if (hasNoProgressYet) {
                return 'Tindak lanjut sudah ditetapkan, tetapi belum ada progress yang dicatat. Mulailah dari keputusan PPEPP, lalu catat perkembangan pertama melalui Update Progres.';
            }
            if (isOverdue) {
                return `Tindak lanjut sedang dikerjakan, tetapi target sudah lewat ${Math.abs(daysRemaining || 0)} hari. Segera perbarui progress atau laporkan hambatan jika masih ada kendala.`;
            }
            return 'Tindak lanjut sedang dalam proses pengerjaan.';
        };

        return (
            <div className="space-y-4">
                {/* Status Card - PRIORITAS UTAMA */}
                <Card className="!p-0 overflow-hidden">
                    <div className={`p-4 bg-gradient-to-r ${perluRevisi ? 'from-amber-500 to-orange-500' :
                        progress >= 100 ? 'from-amber-500 to-orange-500' :
                            isOverdue ? 'from-red-500 to-rose-600' :
                                'from-teal-500 to-teal-600'
                        } text-white`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider opacity-80">Status Tindak Lanjut</span>
                            <PhaseBadge phase="berlangsung" />
                        </div>
                        <div className="flex items-center gap-2">
                            <TlIcon className="w-5 h-5" />
                            <span className="text-lg font-black">{tlStatus.label}</span>
                        </div>
                    </div>

                    <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-neutral-text-secondary">Progress</span>
                            <span className="text-xl font-black text-teal-main">{progress}%</span>
                        </div>
                        <ProgressBar
                            value={progress}
                            color={isOverdue ? 'danger' : progress >= 100 ? 'success' : 'teal'}
                            size="md"
                        />

                        <p className="mt-3 text-sm text-neutral-text-secondary">
                            {getStatusDescription()}
                        </p>
                    </div>
                </Card>

                {/* Target & Deadline */}
                <Card className="!p-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">
                        Target & Waktu
                    </h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CalendarCheck className={`w-4 h-4 ${isOverdue ? 'text-danger' : 'text-neutral-text-secondary'}`} />
                                <span className="text-xs text-neutral-text-secondary">Target</span>
                            </div>
                            <div className="text-right">
                                <p className={`text-sm font-semibold ${isOverdue ? 'text-danger' : ''}`}>
                                    {targetDate ? formatDate(targetDate) : '-'}
                                </p>
                                {daysRemaining !== null && targetDate && (
                                    <p className={`text-[10px] ${isOverdue ? 'text-danger font-bold' :
                                        daysRemaining <= 7 ? 'text-amber-600' : 'text-neutral-text-secondary'
                                        }`}>
                                        {isOverdue
                                            ? `Terlambat ${Math.abs(daysRemaining)} hari`
                                            : `${daysRemaining} hari lagi`
                                        }
                                    </p>
                                )}
                            </div>
                        </div>

                        {data.jumlah_perpanjangan > 0 && (
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
                                <CalendarPlus className="w-3 h-3" />
                                Sudah diperpanjang {data.jumlah_perpanjangan}x
                            </div>
                        )}
                    </div>
                </Card>

                {/* PIC Info */}
                <Card className="!p-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">
                        Penanggung Jawab
                    </h3>

                    {data.pic_user ? (
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white flex items-center justify-center text-lg font-bold shrink-0">
                                {data.pic_user.name?.charAt(0) || 'P'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-neutral-text-primary truncate">
                                    {data.pic_user.name}
                                </p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                    <p className="text-xs text-neutral-text-secondary">
                                        {data.pic_unit?.name || '-'}
                                    </p>
                                    {isPicAktif && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-teal-main/10 text-teal-main rounded font-bold">
                                            Anda
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-neutral-text-secondary italic mb-4">PIC belum ditentukan</p>
                    )}

                    {/* Budget Info */}
                    {Number(data.budget) > 0 && (
                        <div className="pt-3 border-t border-neutral-border">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-neutral-text-secondary">Anggaran</span>
                                <span className="font-semibold text-teal-main">{formatCurrency(Number(data.budget))}</span>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Laporan Terkait */}
                <Card className="!p-4">
                    <details>
                        <summary className="flex items-center justify-between cursor-pointer list-none">
                            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                                Laporan Asal
                            </h3>
                            <ChevronDown className="w-4 h-4 text-neutral-text-secondary" />
                        </summary>
                        <div className="mt-3 pt-3 border-t border-neutral-border space-y-2">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-neutral-text-secondary">Kode</span>
                                <span className="font-mono text-teal-main">{laporanTerkait?.kode || '-'}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-neutral-text-secondary">Judul</span>
                                <span className="font-medium text-right max-w-[150px] truncate">{laporanTerkait?.judul || '-'}</span>
                            </div>
                            {laporanTerkait?.id && (
                                <Link to={`/laporan/${laporanTerkait.id}`} className="block mt-2">
                                    <Button variant="outline" size="sm" className="w-full" icon={<ExternalLink className="w-3.5 h-3.5" />}>
                                        Buka Detail Laporan
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </details>
                </Card>
            </div>
        );
    };

// ========== TERHAMBAT SIDEBAR ==========
const TerhambatSidebar: React.FC<{
    data: any;
    progress: number;
    hambatanTerbaru: HambatanTindakLanjut | null;
    laporanTerkait: any;
    isPicAktif: boolean;
    canDecideHambatan: boolean;
}> = ({ data, progress, hambatanTerbaru, laporanTerkait, isPicAktif, canDecideHambatan }) => (
    <div className="space-y-4">
        {/* Status Terhambat - PRIORITAS */}
        <Card className="!p-0 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-red-500 to-rose-600 text-white">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider opacity-80">Status</span>
                    <PhaseBadge phase="terhambat" />
                </div>
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-lg font-black">Terhambat</span>
                </div>
            </div>

            <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-neutral-text-secondary">Progress saat terhambat</span>
                    <span className="text-xl font-black text-red-600">{progress}%</span>
                </div>
                <ProgressBar value={progress} color="danger" size="md" />

                {isPicAktif ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                        <p className="text-xs font-bold uppercase tracking-widest text-amber-700">
                            Hambatan Dilaporkan
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-amber-800">
                            Hambatan yang Anda laporkan sedang ditinjau oleh pimpinan. Tunggu keputusan untuk langkah selanjutnya.
                        </p>
                    </div>
                ) : canDecideHambatan ? (
                    <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3">
                        <p className="text-xs font-bold uppercase tracking-widest text-red-600">
                            Perlu Keputusan Pimpinan
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-red-700">
                            PIC melaporkan hambatan. Gunakan panel <span className="font-semibold">Keputusan Hambatan</span> di atas untuk memilih langkah lanjutan yang diperlukan.
                        </p>
                    </div>
                ) : (
                    <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-3">
                        <p className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                            Tindak Lanjut Terhambat
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-neutral-700">
                            PIC melaporkan hambatan. Menunggu keputusan pimpinan untuk menentukan langkah selanjutnya.
                        </p>
                    </div>
                )}
            </div>
        </Card>

        {/* Hambatan Detail */}
        {hambatanTerbaru && (
            <Card className="!p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-red-200 bg-gradient-to-r from-red-50 to-rose-50">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <h3 className="text-xs font-bold text-red-800 uppercase tracking-widest">Detail Hambatan</h3>
                    </div>
                </div>
                <div className="p-4 space-y-3">
                    <div>
                        <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase mb-1">Jenis</p>
                        <p className="text-sm font-semibold text-red-700">{hambatanTerbaru.jenis_hambatan}</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase mb-1">Deskripsi</p>
                        <p className="text-sm text-neutral-text-primary">{hambatanTerbaru.deskripsi}</p>
                    </div>
                    {hambatanTerbaru.usulan_solusi && (
                        <div>
                            <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase mb-1">Usulan Solusi</p>
                            <p className="text-sm text-neutral-text-primary">{hambatanTerbaru.usulan_solusi}</p>
                        </div>
                    )}
                    <div className="pt-2 border-t border-neutral-border text-xs text-neutral-text-secondary">
                        Dilaporkan: {formatRelativeTime(hambatanTerbaru.created_at)}
                    </div>
                </div>
            </Card>
        )}

        {/* PIC Info */}
        <Card className="!p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">
                Penanggung Jawab
            </h3>
            {data.pic_user ? (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center font-bold">
                        {data.pic_user.name?.charAt(0) || 'P'}
                    </div>
                    <div>
                        <p className="text-sm font-semibold">{data.pic_user.name}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            <p className="text-xs text-neutral-text-secondary">{data.pic_unit?.name || '-'}</p>
                            {isPicAktif && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-teal-main/10 text-teal-main rounded font-bold">
                                    Anda
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-neutral-text-secondary italic">PIC belum ditentukan</p>
            )}
        </Card>
    </div>
);

// ========== SELESAI SIDEBAR ==========
const SelesaiSidebar: React.FC<{
    data: any;
    progress: number;
    targetDate: string | null;
    tanggalSelesai: Date | null;
    hariTerlambat: number;
    laporanTerkait: any;
}> = ({ data, progress, targetDate, tanggalSelesai, hariTerlambat, laporanTerkait }) => {
    const tepatWaktu = hariTerlambat === 0;
    const durasiPenyelesaian = tanggalSelesai && data.created_at
        ? Math.max(0, differenceInCalendarDays(tanggalSelesai, new Date(data.created_at)))
        : null;

    // State untuk tooltip info
    const [showTimelineInfo, setShowTimelineInfo] = useState(false);

    return (
        <div className="space-y-4">
            {/* Summary Card */}
            <Card className="!p-0 overflow-hidden">
                <div className={`p-5 bg-gradient-to-r ${tepatWaktu ? 'from-emerald-500 to-teal-600' : 'from-amber-500 to-orange-500'
                    } text-white`}>
                    <div className="flex items-center gap-3 mb-3">
                        {tepatWaktu ? (
                            <CheckCircle className="w-8 h-8" />
                        ) : (
                            <AlertTriangle className="w-8 h-8" />
                        )}
                        <div>
                            <p className="text-xs uppercase tracking-wider opacity-80">Hasil Akhir Tindak Lanjut</p>
                            <p className="text-xl font-black">
                                {tepatWaktu ? 'Selesai Tepat Waktu' : `Selesai Terlambat`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm opacity-90">
                        {durasiPenyelesaian !== null && (
                            <span className="flex items-center gap-1">
                                <Timer className="w-4 h-4" />
                                {durasiPenyelesaian} hari
                            </span>
                        )}
                        {!tepatWaktu && hariTerlambat > 0 && (
                            <span className="flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                +{hariTerlambat} hari
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-neutral-bg rounded-xl">
                        <p className="text-2xl font-black text-emerald-600">100%</p>
                        <p className="text-[10px] text-neutral-text-secondary uppercase">Progress PPEPP</p>
                    </div>
                    <div className="text-center p-3 bg-neutral-bg rounded-xl">
                        <p className="text-2xl font-black text-neutral-text-primary">
                            {durasiPenyelesaian ?? '-'}
                        </p>
                        <p className="text-[10px] text-neutral-text-secondary uppercase">Durasi PPEPP</p>
                    </div>
                </div>
            </Card>

            {/* Timeline Summary — dengan tombol ? untuk info level */}
            <Card className="!p-4 border border-emerald-200 bg-emerald-50/35">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                            <CalendarCheck className="w-5 h-5" />
                        </div>
                        <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-800">
                            Timeline Tindak Lanjut
                        </h3>
                    </div>
                    {/* Tombol ? untuk info Ringkasan Mikro */}
                    <button
                        type="button"
                        onClick={() => setShowTimelineInfo(!showTimelineInfo)}
                        className="w-6 h-6 rounded-full bg-emerald-200 text-emerald-700 flex items-center justify-center text-xs font-bold hover:bg-emerald-300 transition-colors shrink-0"
                        title="Apa itu Timeline Tindak Lanjut?"
                    >
                        ?
                    </button>
                </div>

                {/* Info Ringkasan Mikro — tampil saat ? diklik */}
                {showTimelineInfo && (
                    <div className="mb-4 rounded-xl border border-emerald-200 bg-white p-3 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                                <FileText className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-emerald-900">Level PPEPP — Ringkasan Mikro</p>
                                <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                                    Timeline ini membaca eksekusi satu tindak lanjut yang sedang atau sudah dijalankan. Gunakan bagian ini untuk melihat kapan PPEPP dimulai, target kerja PIC, dan kapan hasilnya dikonfirmasi selesai.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    <div className="rounded-xl border border-emerald-100 bg-white/85 px-3 py-3 flex items-center justify-between text-xs">
                        <span className="text-neutral-text-secondary">Dimulai</span>
                        <span className="font-semibold text-neutral-text-primary">{formatDateShort(data.created_at)}</span>
                    </div>
                    {targetDate && (
                        <div className="rounded-xl border border-emerald-100 bg-white/85 px-3 py-3 flex items-center justify-between text-xs">
                            <span className="text-neutral-text-secondary">Target PPEPP</span>
                            <span className="font-semibold text-neutral-text-primary">{formatDateShort(targetDate)}</span>
                        </div>
                    )}
                    <div className="rounded-xl border border-emerald-100 bg-white/85 px-3 py-3 flex items-center justify-between text-xs">
                        <span className="text-neutral-text-secondary">Selesai PPEPP</span>
                        <span className={`font-semibold ${!tepatWaktu ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {tanggalSelesai ? formatDateShort(tanggalSelesai.toISOString()) : '-'}
                        </span>
                    </div>
                </div>
            </Card>

            {/* PIC yang menyelesaikan */}
            {data.pic_user && (
                <Card className="!p-4 border border-emerald-200 bg-emerald-50/25">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-3">
                        PIC Penyelesai PPEPP
                    </h3>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">
                            {data.pic_user.name?.charAt(0) || 'P'}
                        </div>
                        <div>
                            <p className="text-sm font-semibold">{data.pic_user.name}</p>
                            <p className="text-xs text-neutral-text-secondary">{data.pic_unit?.name || '-'}</p>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

// ========== MAIN COMPONENT ==========
const TindakLanjutDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const role = useRole();
    const caps = useCapabilities();
    const { user } = useAuthStore();

    // Core data state
    const [data, setData] = useState<PpeppRekomendasi | null>(null);
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState<PpeppKpi[]>([]);

    // Tab state
    const [activeTab, setActiveTab] = useState<'detail' | 'progress' | 'hambatan' | 'riwayat'>('detail');
    const [hasAutoFocusedHambatan, setHasAutoFocusedHambatan] = useState(false);
    const [showGuide, setShowGuide] = useState(false);

    // Progress form state
    const [showAddProgress, setShowAddProgress] = useState(false);
    const [newProgress, setNewProgress] = useState({
        progress_percentage: '',
        keterangan: '',
        bukti: null as File | null,
    });
    const [saving, setSaving] = useState(false);

    // Hambatan form state
    const [showHambatanForm, setShowHambatanForm] = useState(false);
    const [savingHambatan, setSavingHambatan] = useState(false);
    const [hambatanForm, setHambatanForm] = useState({
        jenis_hambatan: '',
        deskripsi: '',
        usulan_solusi: '',
    });

    // Extend deadline state
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [newDeadline, setNewDeadline] = useState('');
    const [alasanPerpanjang, setAlasanPerpanjang] = useState('');

    // Change PIC state
    const [showChangePicModal, setShowChangePicModal] = useState(false);
    const [picCandidates, setPicCandidates] = useState<any[]>([]);
    const [loadingPicCandidates, setLoadingPicCandidates] = useState(false);
    const [selectedPic, setSelectedPic] = useState('');
    const [alasanGantiPic, setAlasanGantiPic] = useState('');
    const [filterUnitPic, setFilterUnitPic] = useState('');
    const [newDeadlineForPic, setNewDeadlineForPic] = useState('');
    const [alasanTargetBaru, setAlasanTargetBaru] = useState('');

    // Return to RAPIM state
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [alasanKembaliRapim, setAlasanKembaliRapim] = useState('');

    // Tolak selesai state
    const [showTolakSelesaiModal, setShowTolakSelesaiModal] = useState(false);
    const [alasanTolakSelesai, setAlasanTolakSelesai] = useState('');

    // Action loading state
    const [actionLoading, setActionLoading] = useState(false);
    const [submittingKonfirmasiSelesai, setSubmittingKonfirmasiSelesai] = useState(false);

    // Expanded PPEPP IDs
    const [expandedPpeppIds, setExpandedPpeppIds] = useState<Set<number>>(new Set());

    // ========== DATA DERIVATIONS ==========
    const progressHistory = useMemo(() => {
        if (!data?.progress) return [];
        return [...data.progress].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
    }, [data?.progress]);

    const laporanTerkait = (data as any)?.laporan_masalah || (data as any)?.laporan || {};
    const laporanDetailHref = laporanTerkait?.id ? `/laporan/${laporanTerkait.id}` : '/laporan';
    const statusEfektif = (data as any)?.status_efektif || data?.status;
    const statusEfektifNormalized = String(statusEfektif || '').toLowerCase();
    const progressValue = Number(data?.progress_percentage || 0);

    const perluRevisi = useMemo(() => {
        if (!progressHistory || progressHistory.length === 0) return false;
        if (statusEfektif !== 'berlangsung') return false;
        if (progressValue < 100) return false;

        const lastProgress = progressHistory[progressHistory.length - 1];
        const keterangan = (lastProgress?.keterangan || '').toLowerCase();

        const rejectionKeywords = ['ditolak', 'menolak', 'dikembalikan', 'revisi diperlukan', 'belum dapat diterima', 'tolak konfirmasi'];
        return rejectionKeywords.some((keyword) => keterangan.includes(keyword));
    }, [progressHistory, statusEfektif, progressValue]);

    const picHistory = useMemo(() => {
        const history = (data as any)?.riwayat_pic || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.tanggal ? new Date(a.tanggal).getTime() : 0;
            const tb = b?.tanggal ? new Date(b.tanggal).getTime() : 0;
            return ta - tb;
        });
    }, [(data as any)?.riwayat_pic]);

    const riwayatPerpanjangan = useMemo((): RiwayatPerpanjanganItem[] => {
        const history = (data as any)?.riwayat_perpanjangan || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.tanggal ? new Date(a.tanggal).getTime() : 0;
            const tb = b?.tanggal ? new Date(b.tanggal).getTime() : 0;
            return ta - tb;
        });
    }, [(data as any)?.riwayat_perpanjangan]);

    const riwayatPpepp = useMemo(() => {
        const history = (data as any)?.riwayat_ppepp || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
            if (ta === tb) return (a?.id || 0) - (b?.id || 0);
            return ta - tb;
        });
    }, [(data as any)?.riwayat_ppepp]);

    const hambatanList: HambatanTindakLanjut[] = (data as any)?.hambatan_tindak_lanjut || (data as any)?.hambatan || [];
    const hambatanOrdered = hambatanList.slice().sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
    });
    const hambatanTerbaru = hambatanOrdered[0] || null;

    // Unified Timeline
    const unifiedTimeline = useMemo((): UnifiedTimelineEvent[] => {
        if (!data) return [];
        const events: UnifiedTimelineEvent[] = [];

        // PPEPP Events
        riwayatPpepp.forEach((ppepp: any, index: number) => {
            const ppeppIndex = index + 1;
            const isPpeppAktif = ppepp.id === data.id;

            events.push({
                type: 'ppepp_created',
                date: new Date(ppepp.created_at),
                ppeppIndex,
                ppeppId: ppepp.id,
                ppeppStatus: ppepp.status,
                isPpeppAktif,
                data: {
                    program: ppepp.program,
                    pic_user: ppepp.pic_user || ppepp.picUser,
                    pic_unit: ppepp.pic_unit || ppepp.picUnit,
                    rapat: ppepp.rapat_pimpinan,
                    target_date: ppepp.target_date,
                },
            });

            if (ppepp.progress && Array.isArray(ppepp.progress)) {
                ppepp.progress.forEach((prog: any) => {
                    const keterangan = (prog.keterangan || '').toLowerCase();
                    const isRejection = keterangan.includes('ditolak') || keterangan.includes('menolak') || keterangan.includes('dikembalikan');

                    events.push({
                        type: isRejection ? 'rejection' : 'progress',
                        date: new Date(prog.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif,
                        data: {
                            ...prog,
                            pic_at_time: prog.pic_user || ppepp.pic_user || ppepp.picUser || prog.updated_by,
                        },
                    });
                });
            }

            const ppeppStatus = String(ppepp.status).toLowerCase();
            if (ppeppStatus === 'dibatalkan' || ppeppStatus === 'cancelled') {
                const hKembali = ppepp.hambatan_tindak_lanjut?.find((h: any) => h.status === 'dikembalikan_rapim');
                if (hKembali) {
                    events.push({
                        type: 'returned_to_rapim',
                        date: new Date(hKembali.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif: false,
                        data: { catatan: hKembali.catatan_keputusan, alasan: ppepp.alasan_dikembalikan },
                    });
                    events.push({
                        type: 'ppepp_cancelled',
                        date: new Date(hKembali.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif: false,
                        data: { progress_terakhir: ppepp.progress_percentage || 0 },
                    });
                }
            }
        });

        picHistory.forEach((change: any) => {
            events.push({ type: 'pic_change', date: new Date(change.tanggal), data: change });
        });

        riwayatPerpanjangan.forEach((ext: any) => {
            events.push({ type: 'deadline_extended', date: new Date(ext.tanggal), data: ext });
        });

        return events.sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [riwayatPpepp, picHistory, riwayatPerpanjangan, data?.id]);

    // ========== COMPUTED VALUES ==========
    const targetDate = data?.target_date ? new Date(data.target_date) : null;
    const targetDateOnly = targetDate
        ? new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate())
        : null;
    const nowDateOnly = new Date();
    nowDateOnly.setHours(0, 0, 0, 0);
    const daysRemaining = targetDateOnly
        ? Math.ceil((targetDateOnly.getTime() - nowDateOnly.getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    const isMenungguKonfirmasi = statusEfektif === 'validasi';
    const statusLaporanSelesai = ['selesai', 'diarsipkan'].includes(String(laporanTerkait.status || ''));
    const statusTerhambat = statusEfektifNormalized === 'terhambat';
    const dikembalikanRapim = hambatanTerbaru?.status === 'dikembalikan_rapim' || statusEfektifNormalized === 'dibatalkan';
    const statusPpeppSelesai = statusEfektifNormalized === 'selesai';
    const statusFinalPpepp = statusPpeppSelesai || dikembalikanRapim;
    const shouldShowCompletedSummary = statusPpeppSelesai || (statusLaporanSelesai && !dikembalikanRapim && progressValue >= 100);
    const tanggalSelesaiRaw = shouldShowCompletedSummary
        ? (data as any)?.completed_at || (data as any)?.updated_at || laporanTerkait.completed_at || null
        : null;
    const tanggalSelesai = tanggalSelesaiRaw ? new Date(tanggalSelesaiRaw) : null;
    const tanggalSelesaiOnly = tanggalSelesai
        ? new Date(tanggalSelesai.getFullYear(), tanggalSelesai.getMonth(), tanggalSelesai.getDate())
        : null;
    const isOverdue = !!targetDateOnly && !statusFinalPpepp && targetDateOnly < nowDateOnly;

    const hariTerlambatHistoris =
        shouldShowCompletedSummary && targetDateOnly && tanggalSelesaiOnly && tanggalSelesaiOnly > targetDateOnly
            ? Math.ceil((tanggalSelesaiOnly.getTime() - targetDateOnly.getTime()) / (1000 * 60 * 60 * 24))
            : 0;

    const canReportHambatan = statusEfektif === 'berlangsung' && progressValue < 100 && !!data?.target_date && daysRemaining <= 14;
    const canConfirmCompletion = caps.has('confirm_completion');
    const canDecideHambatan = caps.hasAny(['extend_deadline', 'change_pic', 'return_to_rapim']);
    const isPicAktif = user?.id === data?.pic_user_id;
    const jumlahPerpanjangan = Number(data?.jumlah_perpanjangan || 0);
    const batasPerpanjanganTercapai = jumlahPerpanjangan >= 2;
    const ppeppAktifTerbaruId = Number((data as any)?.ppepp_aktif_terbaru_id || 0);
    const isArsipPpeppLama = dikembalikanRapim && ppeppAktifTerbaruId > 0 && ppeppAktifTerbaruId !== data?.id;
    const picUserTampil = (data as any)?.pic_user_tampil || data?.pic_user;
    const picUnitTampil = (data as any)?.pic_unit_tampil || data?.pic_unit;
    const showHambatanActions = canDecideHambatan && statusTerhambat && !statusFinalPpepp;

    const canTandaiSelesai = isPicAktif && statusEfektif === 'berlangsung' && progressValue >= 100 && !dikembalikanRapim && !perluRevisi;
    const isNewlyAssigned = progressValue <= 0 && progressHistory.length === 0 && statusEfektif === 'berlangsung' && !perluRevisi;
    const hasNoProgressYet = isNewlyAssigned;

    // Determine phase
    const getPhase = (): TindakLanjutPhase => {
        if (dikembalikanRapim) return 'dibatalkan';
        if (shouldShowCompletedSummary) return 'selesai';
        if (statusTerhambat) return 'terhambat';
        if (isMenungguKonfirmasi) return 'menunggu_konfirmasi';
        return 'berlangsung';
    };
    const phase = getPhase();

    useEffect(() => {
        setHasAutoFocusedHambatan(false);
    }, [id]);

    useEffect(() => {
        if (showHambatanActions && hambatanOrdered.length > 0 && !hasAutoFocusedHambatan) {
            setActiveTab('hambatan');
            setHasAutoFocusedHambatan(true);
        }
    }, [showHambatanActions, hambatanOrdered.length, hasAutoFocusedHambatan]);

    const openExtendDecision = () => {
        setNewDeadline(data?.target_date ? data.target_date.toString().slice(0, 10) : '');
        setAlasanPerpanjang('');
        setShowExtendModal(true);
    };

    const openChangePicDecision = () => {
        setSelectedPic('');
        setAlasanGantiPic('');
        setFilterUnitPic('');
        setNewDeadlineForPic('');
        setAlasanTargetBaru('');
        setShowChangePicModal(true);
        loadPicCandidates();
    };

    const openReturnToRapimDecision = () => {
        setAlasanKembaliRapim('');
        setShowReturnModal(true);
    };

    const handleBackNavigation = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate(laporanDetailHref);
    };

    const getActionPanelGuidance = () => {
        if (isMenungguKonfirmasi && canConfirmCompletion && !dikembalikanRapim) {
            return 'Tinjau hasil kerja PIC, lalu pilih apakah pengajuan selesai diterima atau perlu dikembalikan untuk perbaikan.';
        }

        if (canTandaiSelesai) {
            return 'Progress sudah 100%. Ajukan konfirmasi selesai agar pimpinan dapat meninjau dan menetapkan hasil akhirnya.';
        }

        if (isPicAktif && phase === 'berlangsung' && !dikembalikanRapim) {
            if (hasNoProgressYet && isOverdue) {
                return 'Belum ada progress yang dicatat dan target sudah lewat. Segera catat perkembangan pertama atau laporkan hambatan bila pekerjaan belum bisa berjalan.';
            }

            if (hasNoProgressYet) {
                return 'Belum ada progress yang dicatat. Mulailah dari keputusan PPEPP, lalu catat perkembangan pertama melalui Update Progres.';
            }

            if (perluRevisi) {
                return 'Lengkapi perbaikan yang diminta, lalu catat pembaruannya melalui Revisi & Update.';
            }

            if (canReportHambatan) {
                return 'Gunakan Update Progres untuk mencatat capaian baru. Laporkan Hambatan jika pekerjaan tersendat atau target terancam tidak tercapai.';
            }

            return 'Gunakan Update Progres untuk mencatat capaian baru sampai pekerjaan siap diajukan sebagai selesai.';
        }

        return 'Pilih tindakan sesuai kondisi tindak lanjut saat ini.';
    };

    const renderTindakLanjutActionPanel = () => (
        <Card className="!p-4">
            <div className="space-y-3">
                <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                        Tindakan yang Tersedia
                    </h3>
                    <p className="mt-1 text-sm text-neutral-text-secondary">
                        {getActionPanelGuidance()}
                    </p>
                </div>

                <div className="space-y-2">
                    {isPicAktif && statusEfektif === 'berlangsung' && (progressValue < 100 || perluRevisi) && !dikembalikanRapim && (
                        <Button
                            variant="primary"
                            size="sm"
                            className="w-full justify-center"
                            onClick={() => setShowAddProgress(true)}
                            icon={<Plus className="w-4 h-4" />}
                        >
                            {perluRevisi ? 'Revisi & Update' : 'Update Progres'}
                        </Button>
                    )}

                    {isPicAktif && canReportHambatan && !dikembalikanRapim && (
                        <Button
                            variant="warning"
                            size="sm"
                            className="w-full justify-center"
                            onClick={() => {
                                setHambatanForm({ jenis_hambatan: '', deskripsi: '', usulan_solusi: '' });
                                setShowHambatanForm(true);
                            }}
                            icon={<AlertCircle className="w-4 h-4" />}
                        >
                            Laporkan Hambatan
                        </Button>
                    )}

                    {canTandaiSelesai && (
                        <Button
                            variant="success"
                            size="sm"
                            className="w-full justify-center"
                            onClick={handleAjukanKonfirmasiSelesai}
                            loading={submittingKonfirmasiSelesai}
                            icon={<CheckCircle className="w-4 h-4" />}
                        >
                            Ajukan Konfirmasi Selesai
                        </Button>
                    )}

                    {isMenungguKonfirmasi && canConfirmCompletion && !dikembalikanRapim && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-center text-danger border-danger/20 hover:bg-danger/10"
                                onClick={() => setShowTolakSelesaiModal(true)}
                                icon={<XCircle className="w-4 h-4" />}
                            >
                                Tolak Pengajuan
                            </Button>
                            <Button
                                variant="success"
                                size="sm"
                                className="w-full justify-center"
                                onClick={handleTerimaSelesai}
                                loading={actionLoading}
                                icon={<CheckCircle className="w-4 h-4" />}
                            >
                                Terima & Selesaikan
                            </Button>
                        </>
                    )}
                </div>

            </div>
        </Card>
    );

    const renderHambatanDecisionPanel = () => (
        <Card className="!p-0 overflow-hidden border border-red-200">
            <div className="px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        <div>
                            <h3 className="text-sm font-bold">Keputusan Hambatan</h3>
                            <p className="text-xs text-white/80">Tentukan langkah lanjutan untuk kendala yang sedang aktif.</p>
                        </div>
                    </div>
                    <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                        Perlu Tindakan
                    </span>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {hambatanTerbaru && (
                    <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">Hambatan Aktif</p>
                                <p className="mt-1 text-sm font-semibold text-red-800">{hambatanTerbaru.jenis_hambatan}</p>
                                <p className="mt-1 text-xs leading-relaxed text-red-700">
                                    {hambatanTerbaru.deskripsi}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActiveTab('hambatan')}
                                className="shrink-0"
                            >
                                Buka Detail
                            </Button>
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    {caps.has('extend_deadline') && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-center"
                            icon={<Clock className="w-4 h-4" />}
                            onClick={openExtendDecision}
                        >
                            Perpanjang Target
                        </Button>
                    )}

                    {caps.has('change_pic') && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-center"
                            icon={<UserCog className="w-4 h-4" />}
                            onClick={openChangePicDecision}
                        >
                            Ganti PIC
                        </Button>
                    )}

                    {caps.has('return_to_rapim') && (
                        <Button
                            variant="danger"
                            size="sm"
                            className="w-full justify-center"
                            icon={<RotateCcw className="w-4 h-4" />}
                            onClick={openReturnToRapimDecision}
                        >
                            Kembalikan ke RAPIM
                        </Button>
                    )}
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                    <p className="text-xs leading-relaxed text-amber-800">
                        Gunakan <span className="font-semibold">Perpanjang Target</span> jika PIC yang sama masih dapat melanjutkan,
                        <span className="font-semibold"> Ganti PIC</span> jika perlu penanggung jawab baru, dan
                        <span className="font-semibold"> Kembalikan ke RAPIM</span> jika hambatan perlu dibahas ulang di tingkat pimpinan.
                    </p>
                </div>
            </div>
        </Card>
    );

    // ========== DATA FETCHING ==========
    useEffect(() => {
        if (id) fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await monitoringService.getTindakLanjutDetail(Number(id));
            setData(res);
            setNewProgress((prev) => ({
                ...prev,
                progress_percentage: res.progress_percentage?.toString() || '',
            }));
            setKpis(res.kpis || []);
        } catch (error) {
            console.error('Error fetching detail:', error);
            toast.error('Gagal memuat detail tindak lanjut');
        } finally {
            setLoading(false);
        }
    };

    // ========== HANDLERS ==========
    const handleAddProgress = async () => {
        if (!newProgress.keterangan.trim()) {
            toast.error('Keterangan wajib diisi');
            return;
        }
        if (!newProgress.progress_percentage) {
            toast.error('Persentase progress wajib diisi');
            return;
        }

        setSaving(true);
        try {
            await monitoringService.addProgress(Number(id), {
                progress_percentage: Number(newProgress.progress_percentage),
                keterangan: newProgress.keterangan,
                bukti: newProgress.bukti || undefined,
            });
            toast.success('Progress berhasil ditambahkan');
            setShowAddProgress(false);
            setNewProgress({ progress_percentage: '', keterangan: '', bukti: null });
            fetchData();
        } catch (e: any) {
            const errorMsg = e?.response?.data?.message || e?.message || 'Gagal menambahkan progress';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const loadPicCandidates = async () => {
        if (!data) return;
        try {
            setLoadingPicCandidates(true);
            const res = await monitoringService.getPicCandidates();
            setPicCandidates(res);
        } catch (e) {
            console.error('Error loading PIC candidates:', e);
            toast.error('Gagal memuat kandidat PIC');
        } finally {
            setLoadingPicCandidates(false);
        }
    };

    const handleReportHambatan = async () => {
        if (!hambatanForm.jenis_hambatan) {
            toast.error('Jenis hambatan wajib dipilih');
            return;
        }
        if (!hambatanForm.deskripsi.trim()) {
            toast.error('Deskripsi hambatan wajib diisi');
            return;
        }

        setSavingHambatan(true);
        try {
            await monitoringService.reportHambatan(Number(id), {
                jenis_hambatan: hambatanForm.jenis_hambatan,
                deskripsi: hambatanForm.deskripsi,
                usulan_solusi: hambatanForm.usulan_solusi || undefined,
            });
            toast.success('Hambatan berhasil dilaporkan');
            setShowHambatanForm(false);
            setHambatanForm({ jenis_hambatan: '', deskripsi: '', usulan_solusi: '' });
            fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal melaporkan hambatan');
        } finally {
            setSavingHambatan(false);
        }
    };

    const handlePerpanjangDeadline = async () => {
        if (!newDeadline) {
            toast.error('Tanggal baru wajib diisi');
            return;
        }
        if (!alasanPerpanjang.trim()) {
            toast.error('Alasan perpanjangan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.perpanjangDeadline(Number(id), newDeadline, alasanPerpanjang);
            toast.success('Deadline berhasil diperpanjang');
            setShowExtendModal(false);
            setNewDeadline('');
            setAlasanPerpanjang('');
            fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal memperpanjang deadline');
        } finally {
            setActionLoading(false);
        }
    };

    const handleGantiPic = async () => {
        if (!selectedPic) {
            toast.error('PIC baru wajib dipilih');
            return;
        }
        if (!alasanGantiPic.trim()) {
            toast.error('Alasan pergantian wajib diisi');
            return;
        }
        if (newDeadlineForPic && !alasanTargetBaru.trim()) {
            toast.error('Alasan perubahan target wajib diisi jika target baru ditetapkan');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.gantiPic(
                Number(id),
                Number(selectedPic),
                alasanGantiPic,
                newDeadlineForPic || undefined,
                alasanTargetBaru || undefined
            );
            toast.success('PIC berhasil diganti' + (newDeadlineForPic ? ' dan target diperbarui' : ''));
            setShowChangePicModal(false);
            setSelectedPic('');
            setAlasanGantiPic('');
            setNewDeadlineForPic('');
            setAlasanTargetBaru('');
            fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal mengganti PIC');
        } finally {
            setActionLoading(false);
        }
    };

    const handleKembalikanRapim = async () => {
        if (!alasanKembaliRapim.trim()) {
            toast.error('Alasan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.kembalikanKeRapim(Number(id), alasanKembaliRapim);
            toast.success('Laporan dikembalikan ke antrean RAPIM');
            setShowReturnModal(false);
            setAlasanKembaliRapim('');
            fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal mengembalikan ke RAPIM');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAjukanKonfirmasiSelesai = async () => {
        setSubmittingKonfirmasiSelesai(true);
        try {
            await monitoringService.ajukanKonfirmasiSelesai(Number(id));
            toast.success('Berhasil diajukan ke menunggu konfirmasi selesai');
            await fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal mengajukan konfirmasi selesai');
        } finally {
            setSubmittingKonfirmasiSelesai(false);
        }
    };

    const handleTolakSelesai = async () => {
        if (!alasanTolakSelesai.trim()) {
            toast.error('Alasan penolakan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.tolakKonfirmasiSelesai(Number(id), alasanTolakSelesai);
            toast.success('Konfirmasi selesai ditolak dan dikembalikan ke PIC');
            setShowTolakSelesaiModal(false);
            setAlasanTolakSelesai('');
            fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal menolak konfirmasi selesai');
        } finally {
            setActionLoading(false);
        }
    };

    const handleTerimaSelesai = async () => {
        setActionLoading(true);
        try {
            await monitoringService.terimaKonfirmasiSelesai(Number(id));
            toast.success('Pekerjaan dikonfirmasi selesai');
            await fetchData();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Gagal menyetujui penyelesaian');
        } finally {
            setActionLoading(false);
        }
    };

    // ========== LOADING STATE ==========
    if (loading) {
        return <Loading variant="page" message="Memuat detail tindak lanjut..." />;
    }

    // ========== NOT FOUND STATE ==========
    if (!data) {
        return (
            <div className="space-y-6">
                <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan', href: '/laporan' }, { label: 'Tidak Ditemukan' }]} />
                <Card>
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-20 h-20 rounded-3xl bg-neutral-bg flex items-center justify-center mb-4">
                            <FileText className="w-10 h-10 text-neutral-text-secondary opacity-30" />
                        </div>
                        <h2 className="text-lg font-bold text-neutral-text-primary mb-2">Data Tidak Ditemukan</h2>
                        <p className="text-sm text-neutral-text-secondary mb-6">Tindak lanjut yang Anda cari tidak tersedia.</p>
                        <Button variant="primary" onClick={() => navigate('/laporan')}>Kembali ke Laporan</Button>
                    </div>
                </Card>
            </div>
        );
    }

    // ========== RENDER ==========
    return (
        <div className="space-y-6 animate-in">
            <Breadcrumb
                items={[
                    { label: 'Dashboard', href: '/dashboard' },
                    { label: 'Detail Laporan', href: laporanDetailHref },
                    { label: 'Detail Tindak Lanjut' },
                ]}
            />

            {/* Alert Banners */}
            {perluRevisi && (
                <Card className="border-2 border-amber-300 bg-amber-50 !p-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-amber-800">Perlu Perbaikan</p>
                            <p className="text-sm text-amber-700 mt-1">
                                Pengajuan selesai ditolak. Lengkapi dokumen/bukti yang diminta, lalu ajukan kembali.
                            </p>
                        </div>
                    </div>
                </Card>
            )}

            {dikembalikanRapim && !isArsipPpeppLama && (
                <Card className="border-2 border-blue-300 bg-blue-50 !p-4">
                    <div className="flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-blue-800">Dikembalikan ke RAPIM</p>
                            <p className="text-sm text-blue-700 mt-1">
                                Tindak lanjut ini sedang menunggu pembahasan ulang di RAPIM berikutnya. PPEPP baru akan dibuat setelah rapat selesai.
                            </p>
                        </div>
                    </div>
                </Card>
            )}

            {isArsipPpeppLama && (
                <Card className="border-2 border-emerald-300 bg-emerald-50 !p-4">
                    <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-emerald-800">Arsip Historis</p>
                            <p className="text-sm text-emerald-700 mt-1">
                                PPEPP ini adalah riwayat yang sudah diarsipkan. Keputusan RAPIM ulang sudah menghasilkan PPEPP baru.
                            </p>
                            <Link to={`/tindak-lanjut/${ppeppAktifTerbaruId}`} className="inline-block mt-2">
                                <Button variant="outline" size="sm" icon={<ExternalLink className="w-3.5 h-3.5" />}>
                                    Buka PPEPP Aktif
                                </Button>
                            </Link>
                        </div>
                    </div>
                </Card>
            )}

            {/* HEADER + ACTIONS */}
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />} onClick={handleBackNavigation}>
                            Kembali
                        </Button>
                        <span className="text-sm font-mono text-teal-main bg-teal-main/5 px-2 py-1 rounded">
                            {laporanTerkait?.kode || '-'}
                        </span>
                        <PhaseBadge phase={phase} />
                    </div>
                    <h1 className="text-xl font-black text-teal-dark tracking-tight">
                        {laporanTerkait?.judul || 'Detail Tindak Lanjut'}
                    </h1>

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge variant="status" status={statusEfektif} size="sm" showDot />
                        {isOverdue && phase !== 'selesai' && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-danger/10 text-danger text-xs font-bold rounded-full">
                                <AlertTriangle className="w-3 h-3" /> Terlambat {Math.abs(daysRemaining)} hari
                            </span>
                        )}
                    </div>
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex flex-wrap items-center gap-2">
                    {laporanTerkait?.id && (
                        <Link to={laporanDetailHref}>
                            <Button
                                variant="outline"
                                size="sm"
                                icon={<FileText className="w-4 h-4" />}
                            >
                                Buka Detail Laporan
                            </Button>
                        </Link>
                    )}

                    <Button
                        variant="outline"
                        size="sm"
                        icon={<Info className="w-4 h-4" />}
                        onClick={() => setShowGuide(true)}
                    >
                        Panduan
                    </Button>

                    <Button
                        variant="outline"
                        size="sm"
                        icon={<Download className="w-4 h-4" />}
                        onClick={async () => {
                            try {
                                const arrayBuffer = await monitoringService.exportTindakLanjutPDF(Number(id));
                                const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `Tindak-Lanjut-${id}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                            } catch {
                                toast.error('Gagal export PDF');
                            }
                        }}
                    >
                        Unduh PDF
                    </Button>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT COLUMN - Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Progress Overview Card */}
                    <Card className="!p-0 overflow-hidden">
                        <div className="px-5 py-4 border-b border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                                        <BarChart3 className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-teal-900">Progress Keseluruhan</h3>
                                        <p className="text-xs text-teal-600">Capaian tindak lanjut saat ini</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-3xl font-black text-teal-main">{progressValue}%</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-5">
                            <ProgressBar
                                value={progressValue}
                                size="lg"
                                color={isOverdue ? 'danger' : progressValue >= 100 ? 'success' : 'teal'}
                            />
                            <div className="flex items-center justify-between mt-3 text-xs text-neutral-text-secondary">
                                <span>0%</span>
                                <span>Target: 100%</span>
                            </div>
                        </div>
                    </Card>

                    {showHambatanActions && (
                        <div className="lg:hidden">
                            {renderHambatanDecisionPanel()}
                        </div>
                    )}

                    {/* Tab Navigation */}
                    <div className="flex items-center gap-1 border-b border-neutral-border overflow-x-auto">
                        {[
                            { id: 'detail' as const, label: 'Keputusan PPEPP', icon: <Award className="w-4 h-4" /> },
                            { id: 'progress' as const, label: 'Riwayat Progres', icon: <TrendingUp className="w-4 h-4" /> },
                            { id: 'hambatan' as const, label: `Hambatan (${hambatanOrdered.length})`, icon: <AlertCircle className="w-4 h-4" /> },
                            { id: 'riwayat' as const, label: 'Riwayat PPEPP', icon: <Clock className="w-4 h-4" /> },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                                    ? 'border-teal-main text-teal-main'
                                    : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
                                    }`}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* TAB CONTENT */}
                    <div className="min-h-[300px]">
                        {/* Detail Tab */}
                        {activeTab === 'detail' && (
                            <Card className="!p-0 overflow-hidden">
                                <div className="px-5 py-4 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                                            <Award className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-emerald-900">Keputusan PPEPP</h3>
                                            <p className="text-xs text-emerald-600">Hasil rapat pimpinan untuk tindak lanjut</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 space-y-5">
                                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                                        {[
                                            { label: 'Program', value: data.program, color: 'from-teal-400 to-teal-600', borderColor: 'border-l-teal-500' },
                                            { label: 'Pengendalian', value: data.pengendalian, color: 'from-blue-400 to-blue-600', borderColor: 'border-l-blue-500' },
                                            { label: 'Evaluasi', value: data.evaluasi, color: 'from-amber-400 to-amber-600', borderColor: 'border-l-amber-500' },
                                            { label: 'Pengembangan', value: data.pengembangan, color: 'from-emerald-400 to-emerald-600', borderColor: 'border-l-emerald-500' },
                                            { label: 'Pelaporan', value: data.pelaporan, color: 'from-purple-400 to-purple-600', borderColor: 'border-l-purple-500' },
                                        ].map((item, i) => (
                                            <div key={i} className={`p-4 bg-neutral-bg/50 rounded-xl border-l-4 ${item.borderColor}`}>
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-2">
                                                    {item.label}
                                                </p>
                                                <p className="text-sm text-neutral-text-primary leading-relaxed">
                                                    {item.value || <span className="italic text-neutral-text-secondary">Tidak ada</span>}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Quick Info Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-neutral-border">
                                        <div className="p-3 bg-neutral-bg rounded-xl text-center">
                                            <Users className="w-5 h-5 text-teal-main mx-auto mb-1" />
                                            <p className="text-[10px] text-neutral-text-secondary uppercase">PIC</p>
                                            <p className="text-xs font-semibold truncate">{picUserTampil?.name || '-'}</p>
                                        </div>
                                        <div className="p-3 bg-neutral-bg rounded-xl text-center">
                                            <Building2 className="w-5 h-5 text-teal-main mx-auto mb-1" />
                                            <p className="text-[10px] text-neutral-text-secondary uppercase">Unit</p>
                                            <p className="text-xs font-semibold truncate">{picUnitTampil?.name || '-'}</p>
                                        </div>
                                        <div className="p-3 bg-neutral-bg rounded-xl text-center">
                                            <Calendar className="w-5 h-5 text-teal-main mx-auto mb-1" />
                                            <p className="text-[10px] text-neutral-text-secondary uppercase">Target</p>
                                            <p className="text-xs font-semibold">{data.target_date ? formatDateShort(data.target_date) : '-'}</p>
                                        </div>
                                        <div className="p-3 bg-neutral-bg rounded-xl text-center">
                                            <FileText className="w-5 h-5 text-teal-main mx-auto mb-1" />
                                            <p className="text-[10px] text-neutral-text-secondary uppercase">Anggaran</p>
                                            <p className="text-xs font-semibold">{formatCurrency(data.budget ? Number(data.budget) : null)}</p>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Progress Tab */}
                        {activeTab === 'progress' && (
                            <Card className="!p-0 overflow-hidden">
                                <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                                                <TrendingUp className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-neutral-text-primary">Riwayat Progress</h3>
                                                <p className="text-xs text-neutral-text-secondary">Timeline lengkap semua event</p>
                                            </div>
                                        </div>
                                        <span className="px-3 py-1 bg-neutral-bg text-neutral-text-secondary text-xs font-bold rounded-full border border-neutral-border">
                                            {unifiedTimeline.length} event
                                        </span>
                                    </div>
                                </div>

                                <div className="p-5">
                                    {unifiedTimeline.length > 0 ? (
                                        <div className="relative">
                                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-main via-neutral-border to-neutral-bg" />

                                            <div className="space-y-4">
                                                {unifiedTimeline.map((item, i) => {
                                                    const isLatest = i === unifiedTimeline.length - 1;

                                                    // Progress Update
                                                    if (item.type === 'progress') {
                                                        const entry = item.data;
                                                        return (
                                                            <div key={`progress-${i}`} className="relative pl-10">
                                                                <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${isLatest ? 'bg-teal-main ring-2 ring-offset-1 ring-teal-main/30' : 'bg-neutral-border'
                                                                    }`} />
                                                                <div className={`p-4 rounded-xl transition-all ${isLatest
                                                                    ? 'bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 shadow-sm'
                                                                    : item.isPpeppAktif
                                                                        ? 'bg-white border border-neutral-border hover:shadow-sm'
                                                                        : 'bg-slate-50 border border-slate-200'
                                                                    }`}>
                                                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${item.isPpeppAktif ? 'bg-teal-main/10 text-teal-main' : 'bg-slate-200 text-slate-700'
                                                                                }`}>
                                                                                {entry.progress_percentage}%
                                                                            </span>
                                                                            <span className="text-xs text-neutral-text-secondary">
                                                                                {formatDateShort(entry.created_at)}
                                                                            </span>
                                                                            {!item.isPpeppAktif && (
                                                                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                                                                    PPEPP #{item.ppeppIndex}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-1.5 text-xs text-neutral-text-secondary">
                                                                            <User className="w-3 h-3" />
                                                                            <span>{entry.pic_at_time?.name || entry.updated_by?.name || '-'}</span>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-sm text-neutral-text-primary leading-relaxed">
                                                                        {entry.keterangan}
                                                                    </p>
                                                                    {entry.bukti_url && (
                                                                        <div className="mt-2 flex items-center gap-2">
                                                                            <FileText className="w-3.5 h-3.5 text-teal-main" />
                                                                            <a href={entry.bukti_url} target="_blank" rel="noopener noreferrer"
                                                                                className="text-xs text-teal-main font-medium hover:underline">
                                                                                Lihat Bukti
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Rejection
                                                    if (item.type === 'rejection') {
                                                        const entry = item.data;
                                                        return (
                                                            <div key={`rejection-${i}`} className="relative pl-10">
                                                                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500" />
                                                                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                                                                    <div className="flex items-start gap-2 mb-2">
                                                                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                                        <div>
                                                                            <p className="text-sm font-semibold text-amber-800">Pengajuan Ditolak</p>
                                                                            <p className="text-xs text-amber-600">{formatDateShort(entry.created_at)}</p>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-sm text-amber-700 mt-2">{entry.keterangan}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // PPEPP Created
                                                    if (item.type === 'ppepp_created') {
                                                        return (
                                                            <div key={`ppepp-created-${i}`} className="relative pl-10">
                                                                <div className={`absolute left-2.5 w-4 h-4 rounded-full flex items-center justify-center ${item.isPpeppAktif ? 'bg-emerald-500 ring-2 ring-offset-1 ring-emerald-300' : 'bg-slate-400'
                                                                    }`}>
                                                                    <Calendar className="w-2.5 h-2.5 text-white" />
                                                                </div>
                                                                <div className={`p-4 rounded-xl ${item.isPpeppAktif ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'
                                                                    }`}>
                                                                    <p className={`text-sm font-bold ${item.isPpeppAktif ? 'text-emerald-700' : 'text-slate-700'}`}>
                                                                        PPEPP ke-{item.ppeppIndex} {item.isPpeppAktif ? '(Aktif)' : '(Historis)'}
                                                                    </p>
                                                                    <div className="mt-2 text-xs space-y-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <Users className="w-3 h-3 text-slate-600" />
                                                                            <span>PIC: {item.data.pic_user?.name || item.data.pic_unit?.name || '-'}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <Target className="w-3 h-3 text-slate-600" />
                                                                            <span>Target: {item.data.target_date ? formatDate(item.data.target_date) : '-'}</span>
                                                                        </div>
                                                                        <p className="text-[10px] text-slate-500 mt-2">{formatDateShort(item.date.toISOString())}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // PIC Change
                                                    if (item.type === 'pic_change') {
                                                        return (
                                                            <div key={`pic-change-${i}`} className="relative pl-10">
                                                                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-blue-500" />
                                                                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <UserCog className="w-4 h-4 text-blue-600" />
                                                                        <span className="text-sm font-semibold text-blue-700">PIC Dialihkan</span>
                                                                    </div>
                                                                    <p className="hidden" aria-hidden="true">
                                                                        {item.data.pic_lama || '-'} {' -> '} {item.data.pic_baru || '-'}
                                                                    </p>
                                                                    <p className="text-sm text-blue-800 font-medium">
                                                                        {item.data.pic_lama || '-'} {' -> '} {item.data.pic_baru || '-'}
                                                                    </p>
                                                                    {item.data.alasan && (
                                                                        <p className="text-xs text-blue-600 mt-1">Alasan: {item.data.alasan}</p>
                                                                    )}
                                                                    <p className="text-[10px] text-blue-500 mt-2">{formatDateShort(item.data.tanggal)}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Deadline Extended
                                                    if (item.type === 'deadline_extended') {
                                                        return (
                                                            <div key={`extended-${i}`} className="relative pl-10">
                                                                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500" />
                                                                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <CalendarPlus className="w-4 h-4 text-amber-600" />
                                                                        <span className="text-sm font-semibold text-amber-700">Deadline Diperpanjang</span>
                                                                    </div>
                                                                    <p className="hidden" aria-hidden="true">
                                                                        {item.data.deadline_lama || '-'} {' -> '} {item.data.deadline_baru || '-'}
                                                                    </p>
                                                                    <p className="text-sm text-amber-800 font-medium">
                                                                        {item.data.deadline_lama || '-'} {' -> '} {item.data.deadline_baru || '-'}
                                                                    </p>
                                                                    {item.data.alasan && (
                                                                        <p className="text-xs text-amber-600 mt-1">Alasan: {item.data.alasan}</p>
                                                                    )}
                                                                    <p className="text-[10px] text-amber-500 mt-2">{formatDateShort(item.data.tanggal)}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Returned to RAPIM
                                                    if (item.type === 'returned_to_rapim') {
                                                        return (
                                                            <div key={`returned-${i}`} className="relative pl-10">
                                                                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-red-500" />
                                                                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <RotateCcw className="w-4 h-4 text-red-600" />
                                                                        <span className="text-sm font-semibold text-red-700">
                                                                            Dikembalikan ke RAPIM (PPEPP ke-{item.ppeppIndex})
                                                                        </span>
                                                                    </div>
                                                                    {item.data.alasan && (
                                                                        <p className="text-sm text-red-800 mt-1">Alasan: {item.data.alasan}</p>
                                                                    )}
                                                                    <p className="text-[10px] text-red-500 mt-2">{formatDateShort(item.date.toISOString())}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // PPEPP Cancelled
                                                    if (item.type === 'ppepp_cancelled') {
                                                        return (
                                                            <div key={`cancelled-${i}`} className="relative pl-10">
                                                                <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-slate-400" />
                                                                <div className="p-3 rounded-xl bg-slate-100 border border-slate-300">
                                                                    <div className="flex items-center gap-2 text-xs text-slate-700">
                                                                        <XCircle className="w-3.5 h-3.5" />
                                                                        <span className="font-semibold">
                                                                            PPEPP ke-{item.ppeppIndex} diarsipkan (Progress: {item.data.progress_terakhir}%)
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mb-4">
                                                <TrendingUp className="w-8 h-8 text-neutral-text-secondary opacity-30" />
                                            </div>
                                            <p className="text-sm font-semibold text-neutral-text-primary mb-1">Belum Ada Progress</p>
                                            <p className="text-xs text-neutral-text-secondary">Progress akan ditampilkan setelah ada update.</p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Hambatan Tab */}
                        {activeTab === 'hambatan' && (
                            <Card className="!p-0 overflow-hidden">
                                <div className="px-5 py-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-rose-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center">
                                                <AlertCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-red-900">Hambatan Tindak Lanjut</h3>
                                                <p className="text-xs text-red-600">Kendala yang dilaporkan selama proses</p>
                                            </div>
                                        </div>
                                        {hambatanTerbaru && (
                                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${hambatanTerbaru.status === 'diperpanjang' ? 'bg-amber-100 text-amber-700' :
                                                hambatanTerbaru.status === 'dikembalikan_rapim' ? 'bg-blue-100 text-blue-700' :
                                                    hambatanTerbaru.status === 'pic_diganti' ? 'bg-purple-100 text-purple-700' :
                                                        hambatanTerbaru.status === 'selesai' ? 'bg-emerald-100 text-emerald-700' :
                                                            'bg-red-100 text-red-700'
                                                }`}>
                                                {hambatanTerbaru.status === 'diperpanjang' ? 'DIPERPANJANG' :
                                                    hambatanTerbaru.status === 'dikembalikan_rapim' ? 'DIKEMBALIKAN' :
                                                        hambatanTerbaru.status === 'pic_diganti' ? 'PIC DIGANTI' :
                                                            hambatanTerbaru.status === 'selesai' ? 'DISELESAIKAN' : 'AKTIF'}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-5">
                                    {hambatanOrdered.length > 0 ? (
                                        <div className="space-y-4">
                                            {hambatanOrdered.map((item) => (
                                                <div key={item.id} className="p-4 rounded-xl bg-neutral-bg border border-neutral-border">
                                                    <div className="flex items-center justify-between text-xs text-neutral-text-secondary mb-2">
                                                        <span>{formatRelativeTime(item.created_at)}</span>
                                                        <span>Pelapor: {item.pelapor?.name || '-'}</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-red-700">{item.jenis_hambatan}</p>
                                                    <p className="text-sm text-neutral-text-primary mt-1">{item.deskripsi}</p>
                                                    {item.usulan_solusi && (
                                                        <div className="text-xs text-neutral-text-secondary mt-2">
                                                            Usulan Solusi: <span className="text-neutral-text-primary">{item.usulan_solusi}</span>
                                                        </div>
                                                    )}
                                                    {item.catatan_keputusan && (
                                                        <div className="mt-3 p-3 rounded-lg bg-white border border-neutral-border">
                                                            <p className="text-xs font-semibold text-neutral-text-primary mb-1">Keputusan Pimpinan:</p>
                                                            <p className="text-xs text-neutral-text-secondary">{item.catatan_keputusan}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                                                <CheckCircle className="w-8 h-8 text-emerald-400" />
                                            </div>
                                            <p className="text-sm font-semibold text-neutral-text-primary mb-1">Tidak Ada Hambatan</p>
                                            <p className="text-xs text-neutral-text-secondary">Tindak lanjut berjalan lancar tanpa kendala.</p>
                                        </div>
                                    )}

                                </div>
                            </Card>
                        )}

                        {/* Riwayat PPEPP Tab */}
                        {activeTab === 'riwayat' && (
                            <Card className="!p-0 overflow-hidden">
                                <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                                            <Clock className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-neutral-text-primary">Riwayat PPEPP</h3>
                                            <p className="text-xs text-neutral-text-secondary">Semua keputusan RAPIM untuk laporan ini</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5">
                                    {riwayatPpepp.length > 0 ? (
                                        <div className="space-y-4">
                                            {riwayatPpepp.map((item, index) => {
                                                const isLatest = index === riwayatPpepp.length - 1;
                                                const statusRiwayat = item.id === riwayatPpepp[riwayatPpepp.length - 1]?.id && item.status !== 'dibatalkan'
                                                    ? 'Aktif' : 'Diarsipkan';
                                                const isExpanded = isLatest || expandedPpeppIds.has(item.id);
                                                const progressPpepp = Number(item.progress_percentage || 0);

                                                return (
                                                    <div key={item.id} className={`rounded-2xl border-2 overflow-hidden ${statusRiwayat === 'Aktif'
                                                        ? 'bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-300'
                                                        : 'bg-white border-neutral-border/50'
                                                        }`}>
                                                        <button
                                                            type="button"
                                                            className="w-full flex items-center justify-between p-4 text-left hover:bg-neutral-bg/30 transition-colors"
                                                            onClick={() => {
                                                                if (isLatest) return;
                                                                setExpandedPpeppIds((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(item.id)) next.delete(item.id);
                                                                    else next.add(item.id);
                                                                    return next;
                                                                });
                                                            }}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <span className={`text-sm font-bold ${statusRiwayat === 'Aktif' ? 'text-teal-800' : 'text-neutral-text-primary'}`}>
                                                                    PPEPP ke-{index + 1}
                                                                </span>
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusRiwayat === 'Aktif' ? 'bg-teal-500 text-white' : 'bg-neutral-bg text-neutral-text-secondary'
                                                                    }`}>
                                                                    {statusRiwayat}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs font-semibold text-neutral-text-secondary">{progressPpepp}%</span>
                                                                <div className="w-20 h-1.5 bg-neutral-border rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-teal-main rounded-full transition-all"
                                                                        style={{ width: `${progressPpepp}%` }}
                                                                    />
                                                                </div>
                                                                {!isLatest && (
                                                                    <ChevronDown className={`w-4 h-4 text-neutral-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                                )}
                                                            </div>
                                                        </button>

                                                        {isExpanded && (
                                                            <div className="px-4 pb-4 space-y-3 border-t border-neutral-border/30">
                                                                <div className="pt-3 text-xs text-neutral-text-secondary">
                                                                    Dibuat: {formatDate(item.created_at)}
                                                                    {item.rapat_pimpinan?.judul ? ` di ${item.rapat_pimpinan.judul}` : ''}
                                                                </div>
                                                                <ProgressBar value={progressPpepp} size="sm" color="teal" />
                                                                <div className="grid grid-cols-1 gap-2">
                                                                    {[
                                                                        { label: 'Program', value: item.program, color: 'border-l-teal-500' },
                                                                        { label: 'Pengendalian', value: item.pengendalian, color: 'border-l-blue-500' },
                                                                        { label: 'Evaluasi', value: item.evaluasi, color: 'border-l-amber-500' },
                                                                        { label: 'Pengembangan', value: item.pengembangan, color: 'border-l-emerald-500' },
                                                                        { label: 'Pelaporan', value: item.pelaporan, color: 'border-l-purple-500' },
                                                                    ].map((field, fi) => (
                                                                        <div key={fi} className={`border-l-4 ${field.color} pl-3 py-1`}>
                                                                            <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">{field.label}</p>
                                                                            <p className="text-xs text-neutral-text-primary">{field.value || '-'}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="flex items-center gap-2 text-xs text-neutral-text-secondary pt-2 border-t border-neutral-border/30">
                                                                    <Users className="w-3 h-3" />
                                                                    <span>PIC: {item.pic_user?.name || item.pic_unit?.name || '-'}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mb-4">
                                                <Clock className="w-8 h-8 text-neutral-text-secondary opacity-30" />
                                            </div>
                                            <p className="text-sm font-semibold text-neutral-text-primary mb-1">Belum Ada Riwayat</p>
                                            <p className="text-xs text-neutral-text-secondary">Riwayat PPEPP akan ditampilkan di sini.</p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}
                    </div>

                    {/* KPI Section */}
                    {kpis.length > 0 && (
                        <Card className="!p-0 overflow-hidden">
                            <div className="px-5 py-4 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                                        <Target className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-purple-900">Key Performance Indicators</h3>
                                        <p className="text-xs text-purple-600">Indikator capaian tindak lanjut</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {kpis.map((kpi) => (
                                    <div key={kpi.id} className="p-4 bg-neutral-bg rounded-xl border border-neutral-border">
                                        <p className="text-xs font-semibold text-neutral-text-secondary mb-2">{kpi.nama_kpi}</p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg font-black text-purple-600">{kpi.current_value || 0}</span>
                                            <span className="text-xs text-neutral-text-secondary">/ {kpi.target_value}</span>
                                        </div>
                                        <ProgressBar
                                            value={kpi.target_value ? (Number(kpi.current_value || 0) / Number(kpi.target_value)) * 100 : 0}
                                            size="sm"
                                            color="primary"
                                        />
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>

                {/* RIGHT COLUMN - Contextual Sidebar */}
                <div className="space-y-4">
                    {(phase === 'berlangsung' || phase === 'menunggu_konfirmasi') && renderTindakLanjutActionPanel()}
                    {showHambatanActions && (
                        <div className="hidden lg:block">
                            {renderHambatanDecisionPanel()}
                        </div>
                    )}

                    {phase === 'berlangsung' && (
                        <BerlangSungSidebar
                            data={data}
                            progress={progressValue}
                            targetDate={data.target_date}
                            isOverdue={isOverdue}
                            daysRemaining={daysRemaining}
                            laporanTerkait={laporanTerkait}
                            perluRevisi={perluRevisi}
                            isPicAktif={isPicAktif}
                            canConfirmCompletion={canConfirmCompletion}
                        />
                    )}

                    {phase === 'terhambat' && (
                        <TerhambatSidebar
                            data={data}
                            progress={progressValue}
                            hambatanTerbaru={hambatanTerbaru}
                            laporanTerkait={laporanTerkait}
                            isPicAktif={isPicAktif}
                            canDecideHambatan={canDecideHambatan}
                        />
                    )}

                    {phase === 'menunggu_konfirmasi' && (
                        <BerlangSungSidebar
                            data={data}
                            progress={progressValue}
                            targetDate={data.target_date}
                            isOverdue={isOverdue}
                            daysRemaining={daysRemaining}
                            laporanTerkait={laporanTerkait}
                            perluRevisi={false}
                            isPicAktif={isPicAktif}
                            canConfirmCompletion={canConfirmCompletion}
                        />
                    )}

                    {phase === 'selesai' && (
                        <SelesaiSidebar
                            data={data}
                            progress={progressValue}
                            targetDate={data.target_date}
                            tanggalSelesai={tanggalSelesai}
                            hariTerlambat={hariTerlambatHistoris}
                            laporanTerkait={laporanTerkait}
                        />
                    )}

                    {phase === 'dibatalkan' && (
                        <Card className="!p-4">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center">
                                    <XCircle className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-neutral-text-primary">Dibatalkan/Diarsipkan</p>
                                    <p className="text-xs text-neutral-text-secondary">PPEPP ini tidak lagi aktif</p>
                                </div>
                            </div>
                            {ppeppAktifTerbaruId > 0 && ppeppAktifTerbaruId !== data.id && (
                                <Link to={`/tindak-lanjut/${ppeppAktifTerbaruId}`}>
                                    <Button variant="primary" size="sm" className="w-full" icon={<ExternalLink className="w-4 h-4" />}>
                                        Buka PPEPP Aktif
                                    </Button>
                                </Link>
                            )}
                        </Card>
                    )}
                </div>
            </div>

            {/* ========== MODALS ========== */}

            <Modal
                isOpen={showGuide}
                onClose={() => setShowGuide(false)}
                title="Panduan Tindak Lanjut"
                size="lg"
                footer={
                    <Button variant="primary" onClick={() => setShowGuide(false)}>
                        Saya Mengerti
                    </Button>
                }
            >
                <div className="space-y-4">
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                        <h3 className="text-sm font-bold text-blue-900">Cara membaca halaman ini</h3>
                        <p className="mt-1 text-sm text-blue-700 leading-relaxed">
                            Halaman ini membantu PIC dan pimpinan membaca keputusan tindak lanjut, memantau progres pekerjaan, serta mengambil langkah saat ada hambatan.
                        </p>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Mulai dari Keputusan PPEPP</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Baca isi keputusan pada tab <span className="font-semibold text-neutral-text-primary">Keputusan PPEPP</span>. Bagian ini menjelaskan pekerjaan yang perlu dijalankan oleh PIC.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Gunakan Update Progres</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Catat perkembangan setiap ada capaian baru. Jika belum ada perkembangan yang dicatat, status akan terbaca sebagai <span className="font-semibold text-neutral-text-primary">Belum Ada Progress</span>.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Laporkan Hambatan saat ada kendala</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Gunakan tombol <span className="font-semibold text-neutral-text-primary">Laporkan Hambatan</span> jika pekerjaan tersendat atau target waktu terancam tidak tercapai.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Ajukan selesai setelah 100%</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Jika progres sudah penuh, PIC dapat mengajukan konfirmasi selesai agar pimpinan meninjau dan menetapkan hasil akhirnya.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Jika Anda pimpinan dan ada hambatan</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Saat ada kendala aktif, halaman ini akan menampilkan panel <span className="font-semibold text-neutral-text-primary">Keputusan Hambatan</span>. Anda tidak perlu membuka tab terlebih dahulu untuk menemukan tindakan yang bisa dipilih.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-neutral-border bg-white p-4">
                            <h4 className="text-sm font-bold text-neutral-text-primary">Pilih keputusan yang paling sesuai</h4>
                            <p className="mt-1 text-sm text-neutral-text-secondary leading-relaxed">
                                Pilih <span className="font-semibold text-neutral-text-primary">Perpanjang Target</span> jika PIC yang sama masih bisa melanjutkan, <span className="font-semibold text-neutral-text-primary">Ganti PIC</span> jika perlu penanggung jawab baru, atau <span className="font-semibold text-neutral-text-primary">Kembalikan ke RAPIM</span> jika masalah perlu dibahas ulang di tingkat pimpinan.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <h4 className="text-sm font-bold text-emerald-900">Yang perlu dipantau setiap hari</h4>
                        <p className="mt-1 text-sm text-emerald-700 leading-relaxed">
                            Perhatikan status tindak lanjut, persentase progres, target waktu, serta riwayat progres. Semakin rapi catatan di halaman ini, semakin mudah pimpinan menilai perkembangan pekerjaan PIC.
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Add Progress Modal */}
            <Modal
                isOpen={showAddProgress}
                onClose={() => setShowAddProgress(false)}
                title="Update Progres"
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowAddProgress(false)}>Batal</Button>
                        <Button variant="primary" onClick={handleAddProgress} loading={saving}>Simpan Progress</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                            Persentase Progress <span className="text-danger">*</span>
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={newProgress.progress_percentage || 0}
                                onChange={(e) => setNewProgress({ ...newProgress, progress_percentage: e.target.value })}
                                className="flex-1"
                            />
                            <span className="text-lg font-black text-teal-main w-14 text-right">
                                {newProgress.progress_percentage || 0}%
                            </span>
                        </div>
                        <ProgressBar value={Number(newProgress.progress_percentage) || 0} size="sm" showLabel={false} color="teal" />
                    </div>

                    <Textarea
                        label="Keterangan"
                        placeholder="Jelaskan progress yang telah dicapai..."
                        value={newProgress.keterangan}
                        onChange={(e) => setNewProgress({ ...newProgress, keterangan: e.target.value })}
                        required
                        rows={4}
                    />

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                            Bukti (Opsional)
                        </label>
                        <label className="flex items-center gap-3 p-3 border border-dashed border-neutral-border rounded-lg cursor-pointer hover:border-teal-light transition-colors">
                            <Upload className="w-5 h-5 text-neutral-text-secondary" />
                            <div>
                                <p className="text-sm font-medium">
                                    {newProgress.bukti ? newProgress.bukti.name : 'Pilih file bukti'}
                                </p>
                                <p className="text-xs text-neutral-text-secondary">PDF, DOC, XLS, JPG, PNG (Max 5MB)</p>
                            </div>
                            <input
                                type="file"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    if (file && file.size > 5 * 1024 * 1024) {
                                        toast.error('Ukuran file maksimal 5MB');
                                        return;
                                    }
                                    setNewProgress({ ...newProgress, bukti: file });
                                }}
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                        </label>
                    </div>
                </div>
            </Modal>

            {/* Laporkan Hambatan Modal */}
            <Modal
                isOpen={showHambatanForm}
                onClose={() => setShowHambatanForm(false)}
                title="Laporkan Hambatan"
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowHambatanForm(false)}>Batal</Button>
                        <Button variant="warning" onClick={handleReportHambatan} loading={savingHambatan}>Simpan Hambatan</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="Jenis Hambatan"
                        placeholder="Pilih jenis hambatan"
                        value={hambatanForm.jenis_hambatan}
                        onChange={(value) => setHambatanForm({ ...hambatanForm, jenis_hambatan: value })}
                        options={JENIS_HAMBATAN_OPTIONS}
                        required
                    />
                    <Textarea
                        label="Deskripsi Hambatan"
                        placeholder="Jelaskan hambatan yang terjadi..."
                        value={hambatanForm.deskripsi}
                        onChange={(e) => setHambatanForm({ ...hambatanForm, deskripsi: e.target.value })}
                        required
                        rows={4}
                    />
                    <Textarea
                        label="Usulan Solusi (Opsional)"
                        placeholder="Tuliskan usulan solusi atau alternatif..."
                        value={hambatanForm.usulan_solusi}
                        onChange={(e) => setHambatanForm({ ...hambatanForm, usulan_solusi: e.target.value })}
                        rows={3}
                    />
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700">
                                Hambatan dapat dilaporkan jika target sudah lewat atau dalam 14 hari ke depan. Pimpinan akan memutuskan langkah selanjutnya.
                            </p>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Perpanjang Target Modal */}
            <Modal
                isOpen={showExtendModal}
                onClose={() => setShowExtendModal(false)}
                title="Perpanjang Target"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowExtendModal(false)}>Batal</Button>
                        <Button variant="primary" onClick={handlePerpanjangDeadline} loading={actionLoading}>Simpan</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Tanggal Target Baru"
                        type="date"
                        value={newDeadline}
                        onChange={(e) => setNewDeadline(e.target.value)}
                        required
                    />
                    <Textarea
                        label="Alasan Perpanjangan"
                        placeholder="Tuliskan alasan perpanjangan..."
                        value={alasanPerpanjang}
                        onChange={(e) => setAlasanPerpanjang(e.target.value)}
                        required
                        rows={3}
                    />
                    {jumlahPerpanjangan > 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-700">
                                Perpanjangan sebelumnya: <strong>{jumlahPerpanjangan}x</strong> (maksimal 2x)
                            </p>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Ganti PIC Modal */}
            <Modal
                isOpen={showChangePicModal}
                onClose={() => setShowChangePicModal(false)}
                title="Ganti PIC"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowChangePicModal(false)}>Batal</Button>
                        <Button variant="primary" onClick={handleGantiPic} loading={actionLoading}>Simpan</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="Filter Unit (opsional)"
                        placeholder="Semua Unit"
                        value={filterUnitPic}
                        onChange={(value) => { setFilterUnitPic(value); setSelectedPic(''); }}
                        options={[
                            { value: '', label: 'Semua Unit' },
                            ...Array.from(
                                new Map(
                                    picCandidates.filter((u) => u.unit).map((u) => [u.unit!.id, u.unit!])
                                ).values()
                            ).sort((a, b) => a.name.localeCompare(b.name)).map((unit) => ({ value: unit.id, label: unit.name })),
                        ]}
                        disabled={loadingPicCandidates}
                    />
                    <Select
                        label="PIC Baru"
                        placeholder={loadingPicCandidates ? 'Memuat kandidat...' : 'Pilih PIC'}
                        value={selectedPic}
                        onChange={(value) => setSelectedPic(value)}
                        options={picCandidates
                            .filter((u) => !filterUnitPic || u.unit_id === Number(filterUnitPic))
                            .map((u) => ({
                                value: u.id,
                                label: `${u.name}${u.unit?.name ? ` - ${u.unit.name}` : ''}`,
                            }))}
                        disabled={loadingPicCandidates}
                        required
                    />
                    <Textarea
                        label="Alasan Pergantian"
                        placeholder="Tuliskan alasan pergantian PIC..."
                        value={alasanGantiPic}
                        onChange={(e) => setAlasanGantiPic(e.target.value)}
                        required
                        rows={3}
                    />
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-700">
                            PIC baru akan menggantikan PIC sebelumnya. Progress tidak akan di-reset.
                        </p>
                    </div>

                    {/* Opsi Perpanjang Target */}
                    <div className="border-t border-neutral-border pt-4">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                            <div>
                                <p className="text-xs font-semibold text-amber-800">
                                    Perbarui Target (Opsional)
                                </p>
                                <p className="text-[11px] text-amber-700 mt-1">
                                    Jika PIC baru memerlukan penyesuaian waktu, tetapkan target baru.
                                    Jika dikosongkan, target mengikuti keputusan RAPIM sebelumnya.
                                </p>
                            </div>
                            {jumlahPerpanjangan >= 2 ? (
                                <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-[11px] text-red-700 font-medium">
                                        ⚠ Batas perpanjangan sudah tercapai (2x). Target tidak dapat diubah lagi.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <Input
                                        label="Target Baru"
                                        type="date"
                                        value={newDeadlineForPic}
                                        onChange={(e) => setNewDeadlineForPic(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    {newDeadlineForPic && (
                                        <Textarea
                                            label="Alasan Perubahan Target"
                                            placeholder="Jelaskan alasan penyesuaian target..."
                                            value={alasanTargetBaru}
                                            onChange={(e) => setAlasanTargetBaru(e.target.value)}
                                            rows={2}
                                            required
                                        />
                                    )}
                                    {jumlahPerpanjangan > 0 && (
                                        <p className="text-[11px] text-amber-700">
                                            Perpanjangan sebelumnya: <strong>{jumlahPerpanjangan}x</strong> (maksimal 2x)
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Kembalikan ke RAPIM Modal */}
            <Modal
                isOpen={showReturnModal}
                onClose={() => setShowReturnModal(false)}
                title="Kembalikan ke RAPIM"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowReturnModal(false)}>Batal</Button>
                        <Button variant="danger" onClick={handleKembalikanRapim} loading={actionLoading}>Kembalikan</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-neutral-text-secondary">
                        Laporan akan dikembalikan ke antrean RAPIM untuk dibahas ulang.
                    </p>
                    <Textarea
                        label="Alasan Pengembalian"
                        placeholder="Tuliskan alasan pengembalian ke RAPIM..."
                        value={alasanKembaliRapim}
                        onChange={(e) => setAlasanKembaliRapim(e.target.value)}
                        required
                        rows={3}
                    />
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700">
                            PPEPP saat ini akan diarsipkan dan PPEPP baru akan dibuat setelah RAPIM ulang.
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Tolak Konfirmasi Selesai Modal */}
            <Modal
                isOpen={showTolakSelesaiModal}
                onClose={() => setShowTolakSelesaiModal(false)}
                title="Tolak Konfirmasi Selesai"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowTolakSelesaiModal(false)}>Batal</Button>
                        <Button variant="danger" onClick={handleTolakSelesai} loading={actionLoading}>Tolak Konfirmasi</Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Textarea
                        label="Alasan Penolakan"
                        placeholder="Jelaskan alasan pekerjaan belum dapat diterima sebagai selesai..."
                        value={alasanTolakSelesai}
                        onChange={(e) => setAlasanTolakSelesai(e.target.value)}
                        required
                        rows={4}
                    />
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700">
                            Status PPEPP akan dikembalikan ke Dalam Proses dan PIC akan diminta memperbaiki pekerjaannya.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TindakLanjutDetail;

